/*
 * @Description: 通用筛选侧边栏状态管理模块
 * @version: 1.0.0
 * @Author: SOLO Coding
 * @Date: 2025-01-20
 */

import commonApi from '@/api/model/common';

const state = {
  // 动态业务类型状态存储
  // 结构: { [businessKey]: { currentFilter, categories, favorites, selectedItems, sidebarVisible, loading, filterCounts } }
  businessStates: {}
}

const mutations = {
  // 初始化业务类型状态
  INIT_BUSINESS_STATE(state, businessKey) {
    if (!state.businessStates[businessKey]) {
      state.businessStates[businessKey] = {
        // 筛选相关
        currentFilter: null,              // 当前激活的筛选条件
        categories: [],                   // 固定分类列表（从hr.js加载）

        // 收藏夹相关
        favorites: [],                    // 用户自定义收藏夹列表

        // 批量操作相关
        selectedItems: [],                // 当前选中的项目ID数组

        // UI状态
        sidebarVisible: true,             // 侧边栏显示状态
        loading: false,                   // 全局加载状态

        // 统计信息
        filterCounts: {}                  // 各筛选条件对应的项目数量
      }
    }
  },

  // 筛选相关
  SET_CURRENT_FILTER(state, { businessKey, filter }) {
    if (state.businessStates[businessKey]) {
      state.businessStates[businessKey].currentFilter = filter
    }
  },
  SET_CATEGORIES(state, { businessKey, categories }) {
    if (state.businessStates[businessKey]) {
      state.businessStates[businessKey].categories = categories
    }
  },
  SET_FILTER_COUNTS(state, { businessKey, counts }) {
    if (state.businessStates[businessKey]) {
      state.businessStates[businessKey].filterCounts = counts
    }
  },

  // 收藏夹相关
  SET_FAVORITES(state, { businessKey, favorites }) {
    if (state.businessStates[businessKey]) {
      state.businessStates[businessKey].favorites = favorites
    }
  },
  ADD_FAVORITE(state, { businessKey, favorite }) {
    if (state.businessStates[businessKey]) {
      state.businessStates[businessKey].favorites.push(favorite)
    }
  },
  UPDATE_FAVORITE(state, { businessKey, id, data }) {
    if (state.businessStates[businessKey]) {
      const index = state.businessStates[businessKey].favorites.findIndex(f => f.id === id)
      if (index !== -1) {
        state.businessStates[businessKey].favorites[index] = {
          ...state.businessStates[businessKey].favorites[index],
          ...data
        }
      }
    }
  },
  DELETE_FAVORITE(state, { businessKey, id }) {
    if (state.businessStates[businessKey]) {
      state.businessStates[businessKey].favorites =
        state.businessStates[businessKey].favorites.filter(f => f.id !== id)
    }
  },

  // 批量操作相关
  SET_SELECTED_ITEMS(state, { businessKey, itemIds }) {
    if (state.businessStates[businessKey]) {
      state.businessStates[businessKey].selectedItems = itemIds
    }
  },
  CLEAR_SELECTED_ITEMS(state, businessKey) {
    if (state.businessStates[businessKey]) {
      state.businessStates[businessKey].selectedItems = []
    }
  },

  // UI状态
  SET_SIDEBAR_VISIBLE(state, { businessKey, visible }) {
    if (state.businessStates[businessKey]) {
      state.businessStates[businessKey].sidebarVisible = visible
    }
  },
  SET_LOADING(state, { businessKey, loading }) {
    if (state.businessStates[businessKey]) {
      state.businessStates[businessKey].loading = loading
    }
  }
}

const actions = {
  // 初始化业务模块
  async initBusinessModule({ commit, dispatch }, businessKey = 'talent') {
    commit('INIT_BUSINESS_STATE', businessKey)
    commit('SET_LOADING', { businessKey, loading: true })
    try {
      await dispatch('loadCategories', businessKey)
      await dispatch('loadFavorites', businessKey)
    } finally {
      commit('SET_LOADING', { businessKey, loading: false })
    }
  },

  // 兼容性方法：初始化人才模块
  async initTalentModule({ dispatch }) {
    return await dispatch('initBusinessModule', 'talent')
  },

  // 加载固定分类
  async loadCategories({ commit }, businessKey = 'talent') {
    try {
      const hrConfig = await import('@/config/hr.js')
      const categories = hrConfig.default.filterSidebarConfig?.[businessKey]?.categories || []
      commit('SET_CATEGORIES', { businessKey, categories })
    } catch (error) {
      console.error('加载分类配置失败:', error)
      commit('SET_CATEGORIES', { businessKey, categories: [] })
    }
  },

  // 收藏夹管理
  async loadFavorites({ commit }, businessKey = 'talent') {
    const response = await commonApi.filterSidebar.list.post({
      business_type: businessKey
    })
    // console.log(response)
    const favorites = response || []

    try {
      commit('SET_FAVORITES', { businessKey, favorites })
    } catch (error) {
      console.error('加载收藏夹失败:', error)
      commit('SET_FAVORITES', { businessKey, favorites: [] })
    }
  },

  async createFavorite({ commit, dispatch }, { businessKey = 'talent', name, color }) {
    // TODO: 替换为API调用
    const response = await commonApi.filterSidebar.save.post({
      businessType: businessKey,
      name: name,
      color: color
    })
    const favorite = response || {}

    commit('ADD_FAVORITE', { businessKey, favorite })
    await dispatch('loadFavorites', businessKey)
    return favorite
  },

  async updateFavorite({ commit, dispatch }, { businessKey = 'talent', id, data }) {
    const updateData = {
      ...data,
      id: id,
      updatedAt: new Date().toISOString()
    }
    console.log(updateData, businessKey, id, data)

    // TODO: 替换为API调用
    try {
      await commonApi.filterSidebar.update.post(updateData)
      commit('UPDATE_FAVORITE', { businessKey, id, data: updateData })
      await dispatch('loadFavorites', businessKey)
    } catch (error) {
      console.error('更新收藏夹失败:', error)
    }
  },

  async deleteFavorite({ commit, dispatch }, { businessKey = 'talent', id }) {
    // TODO: 替换为API调用
    try {
      await commonApi.filterSidebar.remove.get({ id: id })
      commit('DELETE_FAVORITE', { businessKey, id })
      await dispatch('loadFavorites', businessKey)
    } catch (error) {
      console.error('删除收藏夹失败:', error)
    }
  },

  async addToFavorite({ dispatch }, { businessKey = 'talent', favoriteId, itemIds }) {
    const items = []
    itemIds.forEach(itemId => {
      items.push({
        favoriteId: favoriteId,
        itemId: itemId,
        businessType: businessKey
      })
    })
    // TODO: 替换为API调用
    await commonApi.filterSidebar.items.batchAdd.post(items)
    await dispatch('loadFavorites', businessKey)
  },

  async removeFromFavorite({ dispatch, state }, { businessKey = 'talent', favoriteId, itemIds }) {
    console.log('开始移除收藏夹', businessKey, favoriteId, itemIds)
    
    // 获取收藏夹信息，确定正确的businessType
    const businessState = state.businessStates[businessKey]
    if (!businessState || !businessState.favorites) {
      throw new Error(`业务模块 ${businessKey} 未初始化`)
    }
    
    const favorite = businessState.favorites.find(f => f.id === favoriteId)
    if (!favorite) {
      throw new Error('收藏夹不存在')
    }
    
    const items = []
    itemIds.forEach(itemId => {
      // 查找该项目在收藏夹中的实际记录，使用其原始businessType
      const existingItem = favorite.items?.find(item => item.itemId === itemId)
      const actualBusinessType = existingItem?.businessType || businessKey
      
      items.push({
        favoriteId: favoriteId,
        itemId: itemId,
        businessType: actualBusinessType
      })
    })
    
    console.log('删除请求参数:', items)
    // TODO: 替换为API调用
    await commonApi.filterSidebar.items.batchDelete.post(items)
    await dispatch('loadFavorites', businessKey)
  },

  // 筛选操作
  setCurrentFilter({ commit }, { businessKey = 'talent', filter }) {
    commit('SET_CURRENT_FILTER', { businessKey, filter })
  },

  clearFilter({ commit }, businessKey = 'talent') {
    commit('SET_CURRENT_FILTER', { businessKey, filter: null })
  },

  // 批量操作
  setSelectedItems({ commit }, { businessKey = 'talent', itemIds }) {
    commit('SET_SELECTED_ITEMS', { businessKey, itemIds })
  },

  clearSelection({ commit }, businessKey = 'talent') {
    commit('CLEAR_SELECTED_ITEMS', businessKey)
  },

  // UI控制
  toggleSidebar({ commit, state }, businessKey = 'talent') {
    const currentVisible = state.businessStates[businessKey]?.sidebarVisible ?? true
    commit('SET_SIDEBAR_VISIBLE', { businessKey, visible: !currentVisible })
  },

  setSidebarVisible({ commit }, { businessKey = 'talent', visible }) {
    commit('SET_SIDEBAR_VISIBLE', { businessKey, visible })
  },

  // 统计信息更新
  updateFilterCounts({ commit }, { businessKey = 'talent', counts }) {
    commit('SET_FILTER_COUNTS', { businessKey, counts })
  },

  // 兼容性方法
  setSelectedTalents({ dispatch }, talentIds) {
    return dispatch('setSelectedItems', { businessKey: 'talent', itemIds: talentIds })
  }
}

const getters = {
  // 获取业务状态
  getBusinessState: state => businessKey => {
    return state.businessStates[businessKey] || {
      currentFilter: null,
      categories: [],
      favorites: [],
      selectedItems: [],
      sidebarVisible: true,
      loading: false,
      filterCounts: {}
    }
  },

  // 获取当前筛选条件
  getCurrentFilter: (state, getters) => (businessKey = 'talent') => {
    return getters.getBusinessState(businessKey).currentFilter
  },

  // 获取所有筛选项（分类+收藏夹）
  getAllFilterItems: (state, getters) => (businessKey = 'talent') => {
    const businessState = getters.getBusinessState(businessKey)
    return [...businessState.categories, ...businessState.favorites]
  },

  // 根据ID获取收藏夹
  getFavoriteById: (state, getters) => (businessKey = 'talent', id) => {
    const businessState = getters.getBusinessState(businessKey)
    return businessState.favorites.find(f => f.id === id)
  },

  // 获取选中项目数量
  getSelectedCount: (state, getters) => (businessKey = 'talent') => {
    return getters.getBusinessState(businessKey).selectedItems.length
  },

  // 检查是否有选中的项目
  hasSelectedItems: (state, getters) => (businessKey = 'talent') => {
    return getters.getBusinessState(businessKey).selectedItems.length > 0
  },

  // 获取指定筛选条件的项目数量
  getFilterCount: (state, getters) => (businessKey = 'talent', filterId) => {
    const businessState = getters.getBusinessState(businessKey)
    return businessState.filterCounts[filterId] || 0
  },

  // 检查侧边栏是否可见
  isSidebarVisible: (state, getters) => (businessKey = 'talent') => {
    return getters.getBusinessState(businessKey).sidebarVisible
  },

  // 检查是否正在加载
  isLoading: (state, getters) => (businessKey = 'talent') => {
    return getters.getBusinessState(businessKey).loading
  },

  // 获取收藏夹列表
  getFavorites: (state, getters) => (businessKey = 'talent') => {
    return getters.getBusinessState(businessKey).favorites
  },

  // 获取固定分类列表
  getCategories: (state, getters) => (businessKey = 'talent') => {
    return getters.getBusinessState(businessKey).categories
  },

  // 获取选中项目列表
  getSelectedItems: (state, getters) => (businessKey = 'talent') => {
    return getters.getBusinessState(businessKey).selectedItems
  },

  // 兼容性getters（默认使用talent）
  getCurrentFilterLegacy: (state, getters) => getters.getCurrentFilter('talent'),
  getAllFilterItemsLegacy: (state, getters) => getters.getAllFilterItems('talent'),
  getFavoriteByIdLegacy: (state, getters) => id => getters.getFavoriteById('talent', id),
  getSelectedCountLegacy: (state, getters) => getters.getSelectedCount('talent'),
  hasSelectedTalents: (state, getters) => getters.hasSelectedItems('talent'),
  getFilterCountLegacy: (state, getters) => filterId => getters.getFilterCount('talent', filterId),
  isSidebarVisibleLegacy: (state, getters) => getters.isSidebarVisible('talent'),
  isLoadingLegacy: (state, getters) => getters.isLoading('talent'),
  getFavoritesLegacy: (state, getters) => getters.getFavorites('talent'),
  getCategoriesLegacy: (state, getters) => getters.getCategories('talent')
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}